import React, { PureComponent, ReactNode } from "react";
import { NoteAccidental } from "../../../wasm/pkg/wasm";
import { AppContextType } from "../../AppContext";
import { buildTranscriptionPreview, initBlankTranscriptionPreview, TranscriptionPreview } from "../../functions/buildTranscriptionPreview";
import { transcribeAudioHistory } from "../../functions/transcribeAudioHistory";
import { AudioHistoryState } from "../../statemachines/AudioHistoryState";
import InfoCard from "../layout_reuse/InfoCard";
import OptionMenuOneLineToggle from "../theme_reuse/OptionMenuOneLineToggle";
import TextButton from "../theme_reuse/TextButton";
import WarningText from "../theme_reuse/WarningText";
import CloseableModal from "./CloseableModal";
import "./TranscriptionPopup.css";
import { MINOR_ROOTS_WITH_SHARP_FLAT_CHOICE } from "../../functions/chooseSharpsFlatsGivenMinorRoot";

interface Props {
  audioHistory: AudioHistoryState;
  onTranscribe: (audioHistory: AudioHistoryState) => void;
}
interface State {
  howManyHalfSteps: number;
  targetSharpFlat: NoteAccidental;
  preview: TranscriptionPreview | undefined;
}

const sharpFlatOptions: Array<NoteAccidental> = ["Sharp", "Flat"];
function printSharpFlatOption(s: NoteAccidental): string {
  return `Prefer ${s}s`;
}

class TranscriptionPopup extends PureComponent<Props, State> {
  declare context: React.ContextType<typeof AppContextType>;

  constructor(props: Props) {
    super(props);

    this.state = {
      howManyHalfSteps: 0,
      preview: undefined,
      targetSharpFlat: "Flat",
    };
  }

  componentDidUpdate(oldProps: Props) {
    if (
      oldProps.audioHistory.entryCounter !== this.props.audioHistory.entryCounter ||
      oldProps.audioHistory.primaryScaleId !== this.props.audioHistory.primaryScaleId
    ) {
      const { audioHistory } = this.props;
      const entries = Object.keys(audioHistory.chordsInScales).map((sid) => initBlankTranscriptionPreview(audioHistory, sid));
      const preview = { entries };
      // This barely matters.  Only the 3 weirdest supported scales actually consider the choice.
      const isFlat =
        audioHistory.primaryScaleId &&
        audioHistory.chordsInScales[audioHistory.primaryScaleId].scale.family.scale_uses_sharps_or_flats === "Flat";
      this.setState({ howManyHalfSteps: 0, preview, targetSharpFlat: isFlat ? "Flat" : "Sharp" });
    }
  }

  handleCloseModal = () => {
    this.context.dispatch({ type: "HideTranscriptionPopup" });
  };

  handleSave = () => {
    const audioHistory = transcribeAudioHistory(this.props.audioHistory, this.state.howManyHalfSteps, this.state.targetSharpFlat);
    this.props.onTranscribe(audioHistory);
    this.context.dispatch({ type: "HideTranscriptionPopup" });
  };

  handleAdjustHalfSteps = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.preview || this.props.audioHistory.isPlaceholder) {
      return;
    }
    const intVal = Number.parseInt(e.target.value);
    const howManyHalfSteps = Number.isInteger(intVal) ? intVal : 0;
    const preview = buildTranscriptionPreview(this.state.preview, howManyHalfSteps, this.state.targetSharpFlat);
    this.setState({ preview, howManyHalfSteps });
  };

  editSharpFlat = (targetSharpFlat: NoteAccidental) => {
    if (!this.state.preview || this.props.audioHistory.isPlaceholder) {
      return;
    }
    const preview = buildTranscriptionPreview(this.state.preview, this.state.howManyHalfSteps, targetSharpFlat);
    this.setState({ preview, targetSharpFlat });
  };

  renderTranscriptionBeforeAfter(): ReactNode {
    if (!this.state.preview || this.props.audioHistory.isPlaceholder) {
      return undefined;
    }
    const isPreferMajor = this.context.keySelectionOptions.isPreferMajor;
    return (
      <>
        {this.state.preview.entries.map(({ original, target }) => {
          const originalName = isPreferMajor ? original.majorTonicName : original.minorTonicName;
          const targetName = isPreferMajor ? target.majorTonicName : target.minorTonicName;
          const majMin = isPreferMajor ? "major" : "minor";
          return (
            <div key={original.minorTonicId} className="transci-preview-row">
              <span>
                {originalName} {majMin}
              </span>
              <span>--&gt;</span>
              <span>
                {" "}
                {targetName} {majMin}
              </span>
            </div>
          );
        })}
      </>
    );
  }

  isSharpsFlatsChoiceAvailable = () => {
    const { preview, howManyHalfSteps } = this.state;
    if (preview?.entries.length && howManyHalfSteps !== 0) {
      return preview.entries.some((p) => MINOR_ROOTS_WITH_SHARP_FLAT_CHOICE.includes(p.target.minorTonicId));
    }
    return false;
  };

  render(): ReactNode {
    if (this.context.showTranscriptionPopup) {
      const { howManyHalfSteps, targetSharpFlat } = this.state;
      const showSharpFlatChoice = this.isSharpsFlatsChoiceAvailable();

      return (
        <CloseableModal onClose={this.handleCloseModal}>
          <InfoCard>
            <h2>Transcribe</h2>
            <p>Adjust the pitch of all notes and chords to a higher or lower key.</p>
          </InfoCard>
          <InfoCard>
            <div className="transci-form-options-area">
              <div className="transcri-half-step-prompt">
                <label className="transci-num-input-label" htmlFor="transcri-edit-num">
                  By How Many Half Steps?
                </label>
                <input
                  className="transci-num-input"
                  id="transcri-edit-num"
                  type="number"
                  min="-11"
                  max="11"
                  value={howManyHalfSteps}
                  onChange={this.handleAdjustHalfSteps}
                ></input>
              </div>
              {showSharpFlatChoice ? (
                <OptionMenuOneLineToggle
                  optionShortNames={sharpFlatOptions}
                  selected={targetSharpFlat}
                  printShortName={printSharpFlatOption}
                  onChange={this.editSharpFlat}
                />
              ) : undefined}
            </div>
            <div className="transcri-form-preview-area">
              <div className="transcri-form-preview-header">Scales Before --&gt; After</div>
              <div className="transcri-form-preview-scales">{this.renderTranscriptionBeforeAfter()}</div>
            </div>
            <div className="transcri-form-submit-area">
              <TextButton big disabled={howManyHalfSteps === 0} onClick={this.handleSave} text="Apply Changes" />
            </div>
            <p>
              <WarningText text="Wait until you're done processing the audio file before transcribing.  Any new audio will not be transcribed, resulting in a mismatch." />
            </p>
          </InfoCard>
        </CloseableModal>
      );
    }
    return undefined;
  }
}

// Not sure if actually needed.  The context knows whether this thing is open... must close it.
TranscriptionPopup.contextType = AppContextType;

export default TranscriptionPopup;
